import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import CVPreview from "@/components/CVPreview";
import { useReactToPrint } from "react-to-print";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareCVPage = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [cvData, setCvData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cvPreviewRef = useRef();

  useEffect(() => {
    const fetchCV = async () => {
      try {
        const response = await axios.get(`${API}/cv/shared/${shareToken}`);
        setCvData(response.data.cvData);
      } catch (error) {
        toast.error("CV not found or link expired");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (shareToken) {
      fetchCV();
    }
  }, [shareToken]);

  const handlePrint = useReactToPrint({
    content: () => cvPreviewRef.current,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading CV...</p>
        </div>
      </div>
    );
  }

  if (!cvData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="font-heading text-3xl font-light">CV Not Found</h1>
          <p className="text-muted-foreground">This CV link may have expired or been removed.</p>
          <Button onClick={() => navigate("/")} className="rounded-none">
            <Home className="mr-2 h-4 w-4" />
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border p-4 sticky top-0 z-10 no-print">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="font-heading text-xl font-medium">
            {cvData.fullName}'s CV
          </h1>
          <div className="flex gap-3">
            <Button
              onClick={handlePrint}
              variant="outline"
              className="rounded-none border-border"
              data-testid="print-cv-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="ghost"
              className="rounded-none"
            >
              <Home className="mr-2 h-4 w-4" />
              Create Your CV
            </Button>
          </div>
        </div>
      </div>

      <div className="py-8 px-6 bg-preview-bg flex justify-center">
        <div className="transform scale-75 md:scale-90 origin-top">
          <CVPreview ref={cvPreviewRef} cvData={cvData} />
        </div>
      </div>
    </div>
  );
};

export default ShareCVPage;
